import {APP_INITIALIZER, ErrorHandler, NgModule} from "@angular/core";
import {BrowserModule} from "@angular/platform-browser";
import {RouterModule, UrlSerializer} from "@angular/router";
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr} from "@angular/common/http";
import {LowerCaseUrlSerializer} from "src/routing/lowercase-url-serializer";
import {AppRoutingModule} from "./app-routing.module";
import {AppComponent} from "./app.component";
import {PtpComponent} from "./ptp/ptp.component";
import {PtpGridSkeletonComponent} from "./ptp/ptp-grid-skeleton/ptp-grid-skeleton.component";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {OrderByPipe} from "src/pipes/order-by.pipe";
import {NgbModule} from "@ng-bootstrap/ng-bootstrap";
import {BaseChartDirective, provideCharts, withDefaultRegisterables} from "ng2-charts";
import {PtpFormsComponent} from "./ptp/ptp-forms/ptp-forms.component";
import {PoInvoiceComponent} from "./ptp/po-invoice/po-invoice.component";
import {OrderMonitorComponent} from "./ptp/order-monitor/order-monitor.component";
import {PaymentComponent} from "./ptp/payment/payment.component";
import {GoodsReceiptComponent} from "./ptp/goods-receipt/goods-receipt.component";
import {AuditLogComponent} from "./ptp/audit-log/audit-log.component";
import {PrComponent} from "./ptp/pr/pr.component";
import {PoComponent} from "./ptp/po/po.component";
import {ARReportComponent} from "./report/arreport/arreport.component";
import {CustomersComponent} from "./report/arreport/customers/customers.component";
import {CustomerEditComponent} from "./report/arreport/customers/customer-edit/customer-edit.component";
import {FINReportComponent} from "./report/finreport/finreport.component";
import {Ng2SearchPipe} from "src/pipes/filter.pipe";
import {CommonModule, DatePipe, JsonPipe, registerLocaleData} from "@angular/common";
import {SafeTooltipDirective} from "../directives/safe-tooltip.directive";
import {ScanDirectoryComponent} from "./configure/scan-directory/scan-directory.component";
import {ScanDirectoryEditComponent} from "./configure/scan-directory/scan-directory-edit/scan-directory-edit.component";
import {QRCodeComponent} from "angularx-qrcode";
import {ProfileComponent} from "./profile/profile.component";
import {TwoFactorComponent} from "./profile/two-factor/two-factor.component";
import {ToggleSwitchComponent} from "./components/toggle-switch.component";
import {ChangePasswordComponent} from "./profile/change-password/change-password.component";
import {ChangeEmailComponent} from "./profile/change-email/change-email.component";
import {LogoutComponent} from "./profile/logout/logout.component";
import {InternalSettingComponent} from "./configure/internal-setting/internal-setting.component";
import {PTPDepartmentComponent} from "./configure/ptp-department/ptp-department.component";
import {PTPDepartmentEditComponent} from "./configure/ptp-department/ptp-department-edit/ptp-department-edit.component";
import {PTPLocationComponent} from "./configure/ptp-location/ptp-location.component";
import {PTPLocationEditComponent} from "./configure/ptp-location/ptp-location-edit/ptp-location-edit.component";
import {PTPMetaDataMapComponent} from "./configure/ptp-metadata-map/ptp-metadata-map.component";
import {PTPMetaDataMapEditComponent} from "./configure/ptp-metadata-map/ptp-metadata-map-edit/ptp-metadata-map-edit.component";
import {PTPStepComponent} from "./configure/ptp-step/ptp-step.component";
import {PTPStepEditComponent} from "./configure/ptp-step/ptp-step-edit/ptp-step-edit.component";
import {EQConnectorKeyComponent} from "./configure/eq-connector-key/eq-connector-key.component";
import {LogsComponent} from "./configure/logs/logs.component";
import {InternalSettingEditComponent} from "./configure/internal-setting/internal-setting-edit/internal-setting-edit.component";
import {ManageIndexComponent} from "./configure/manage-index/manage-index.component";
import {UserComponent} from "./configure/user/user.component";
import {UserEditComponent} from "./configure/user/user-edit/user-edit.component";
import {ConsentComponent} from "./account/consent/consent.component";
import {AiSettingComponent} from "./configure/ai-setting/ai-setting.component";
import {SearchComponent} from "./search/search.component";
import {AboutComponent} from "./about/about.component";
import {UploadComponent} from "./upload/upload.component";
import {provideNzI18n} from "ng-zorro-antd/i18n";
import {en_US} from "ng-zorro-antd/i18n";
import en from "@angular/common/locales/en";
import {provideAnimationsAsync} from "@angular/platform-browser/animations/async";
import {NzButtonModule} from "ng-zorro-antd/button";
import {NzDropdownModule} from "ng-zorro-antd/dropdown";
import {NzIconModule, provideNzIcons} from "ng-zorro-antd/icon";
import {NzCollapseModule} from "ng-zorro-antd/collapse";
import {NzTabsModule} from "ng-zorro-antd/tabs";
import {NzTooltipModule} from "ng-zorro-antd/tooltip";
import {NzPopoverModule} from "ng-zorro-antd/popover";
import {NzTagModule} from "ng-zorro-antd/tag";
import {NzDescriptionsModule} from "ng-zorro-antd/descriptions";
import {NzRadioModule} from "ng-zorro-antd/radio";
import {NzSelectModule} from "ng-zorro-antd/select";
import {NzDatePickerModule} from "ng-zorro-antd/date-picker";
import {NzTimePickerModule} from "ng-zorro-antd/time-picker";
import {NzModalModule} from "ng-zorro-antd/modal";
import {NzAlertModule} from "ng-zorro-antd/alert";
import {NzBadgeModule} from "ng-zorro-antd/badge";
import {
    FileExcelFill,
    RedoOutline,
    SaveFill,
    SaveOutline,
    UndoOutline,
    BulbFill,
    BulbOutline,
    ReadFill,
    DownloadOutline,
    InfoCircleOutline,
    LoadingOutline,
    UploadOutline,
    CheckOutline,
    CloseOutline,
    CloseCircleFill,
    CopyOutline,
    DownOutline,
    EditOutline,
    PlusCircleOutline,
    AimOutline,
    ZoomInOutline,
    ZoomOutOutline
} from "@ant-design/icons-angular/icons";
import {IconDefinition} from "@ant-design/icons-angular";
import {NzInputModule, NzInputAddonBeforeDirective, NzTextareaCountComponent} from "ng-zorro-antd/input";
import {NzInputNumberModule} from "ng-zorro-antd/input-number";
import {NzUploadComponent, NzUploadModule} from "ng-zorro-antd/upload";
import {NzSpaceModule} from "ng-zorro-antd/space";
import {NzColDirective} from "ng-zorro-antd/grid";
import {ScrollingModule} from "@angular/cdk/scrolling";
import {TextFieldModule} from "@angular/cdk/text-field";
import {NzTableModule} from "ng-zorro-antd/table";
import {NzSwitchModule} from "ng-zorro-antd/switch";
import {NzProgressModule} from "ng-zorro-antd/progress";
import {GroupComponent} from "./configure/group/group.component";
import {GroupEditComponent} from "./configure/group/group-edit/group-edit.component";
import {KeywordComponent} from "./configure/keyword/keyword.component";
import {KeywordEditComponent} from "./configure/keyword/keyword-edit/keyword-edit.component";
import {DocumentEditComponent} from "./document/document-edit/document-edit.component";
import {DocumentTypeComponent} from "./configure/document-type/document-type.component";
import {DocumentTypeEditComponent} from "./configure/document-type/document-type-edit/document-type-edit.component";
import {ApComponent} from "./ap/ap.component";
import {ApLogComponent} from "./ap/ap-log/ap-log.component";
import {ApPreviewComponent} from "./ap/ap-preview/ap-preview.component";
import {DXSettingsComponent} from "./tenant/dx-settings/dx-settings.component";
import {ProcessScanComponent} from "./scan/process-scan/process-scan.component";
import {NzPopconfirmModule} from "ng-zorro-antd/popconfirm";
import {NzCheckboxModule} from "ng-zorro-antd/checkbox";
import {NzNotificationService} from "ng-zorro-antd/notification";
import {NzFormModule} from "ng-zorro-antd/form";
import {NzSkeletonModule} from "ng-zorro-antd/skeleton";
import {NzEmptyModule} from "ng-zorro-antd/empty";
import {ApcheckSettingComponent} from "./configure/apcheck-setting/apcheck-setting.component";
import {ScanListComponent} from "./scan/scans/scans.component";
import {ScanListTabComponent} from "./scan/scans/scan-list-tab/scan-list-tab.component";
import {ScanJobsTabComponent} from "./scan/scans/scan-jobs-tab/scan-jobs-tab.component";
import {UserInfoComponent} from "./profile/user-info/user-info.component";
import {Error404Component} from "./error/error404/error404.component";
import {Error500Component} from "./error/error500/error500.component";
import {ErrorInvalidComponent} from "./error/error-invalid/error-invalid.component";
import {ErrorInstanceNotFoundComponent} from "./error/error-instance-not-found/error-instance-not-found.component";
import {ErrorComponent} from "./error/error.component";
import {Monitor} from "./services/monitor.service";
import {GlobalErrorHandler} from "./services/global-error-handler.service";
import {LoginComponent} from "./account/login/login.component";
import {ForgotPasswordComponent} from "./account/forgot-password/forgot-password.component";
import {ResetPasswordComponent} from "./account/reset-password/reset-password.component";
import {AuthService} from "./services/auth.service";
import {AuthInterceptor} from "./services/auth.interceptor";
import {PerformaModulesService} from "./services/performa-modules.service";
import {ZonalTemplateListComponent} from "./configure/zonal-template/zonal-template-list.component";
import {ConfigureLandingComponent} from "./configure/configure-landing/configure-landing.component";
import {ZonalTemplateEditComponent} from "./configure/zonal-template/zonal-template-edit.component";
import {ZonalTemplateFailedCreateComponent} from "./configure/zonal-template/zonal-template-failed-create/zonal-template-failed-create.component";
import {ZonalTemplateCreateComponent} from "./configure/zonal-template/zonal-template-create/zonal-template-create.component";
import {ZonalRegionDirective} from "../directives/zonal-region.directive";
import {ScanThumbDirective} from "../directives/scan-thumb.directive";
import {provideNzDateFnsAdapter} from "ng-zorro-antd/core/time";

registerLocaleData(en);
const icons: IconDefinition[] = [
    SaveFill,
    SaveOutline,
    FileExcelFill,
    BulbOutline,
    UndoOutline,
    RedoOutline,
    ReadFill,
    DownloadOutline,
    InfoCircleOutline,
    LoadingOutline,
    UploadOutline,
    CheckOutline,
    CloseOutline,
    CloseCircleFill,
    CopyOutline,
    DownOutline,
    EditOutline,
    PlusCircleOutline,
    AimOutline,
    ZoomInOutline,
    ZoomOutOutline
];

@NgModule({
    declarations: [
        AppComponent,
        PtpComponent,
        PtpGridSkeletonComponent,
        OrderByPipe,
        SearchComponent,
        AboutComponent,
        UploadComponent,
        ScanDirectoryComponent,
        ScanDirectoryEditComponent,
        ConsentComponent,
        AiSettingComponent,
        PtpFormsComponent,
        PoInvoiceComponent,
        ProfileComponent,
        ScanListComponent,
        ScanListTabComponent,
        ScanJobsTabComponent,
        TwoFactorComponent,
        OrderMonitorComponent,
        PaymentComponent,
        AuditLogComponent,
        PoComponent,
        Ng2SearchPipe,
        GoodsReceiptComponent,
        PrComponent,
        ChangePasswordComponent,
        ChangeEmailComponent,
        UserInfoComponent,
        ARReportComponent,
        CustomersComponent,
        CustomerEditComponent,
        FINReportComponent,
        LogoutComponent,
        SafeTooltipDirective,
        ProcessScanComponent,
        InternalSettingComponent,
        PTPDepartmentComponent,
        PTPDepartmentEditComponent,
        PTPLocationComponent,
        PTPLocationEditComponent,
        PTPMetaDataMapComponent,
        PTPMetaDataMapEditComponent,
        PTPStepComponent,
        PTPStepEditComponent,
        EQConnectorKeyComponent,
        LogsComponent,
        InternalSettingEditComponent,
        ManageIndexComponent,
        UserComponent,
        UserEditComponent,
        GroupComponent,
        GroupEditComponent,
        KeywordComponent,
        KeywordEditComponent,
        DocumentEditComponent,
        DocumentTypeComponent,
        DocumentTypeEditComponent,
        ApComponent,
        ApLogComponent,
        ApPreviewComponent,
        ApcheckSettingComponent,
        ErrorComponent,
        Error404Component,
        Error500Component,
        ErrorInvalidComponent,
        ErrorInstanceNotFoundComponent,
        LoginComponent,
        ForgotPasswordComponent,
        ResetPasswordComponent,
        ZonalTemplateListComponent,
        ConfigureLandingComponent,
        ZonalTemplateEditComponent,
        ZonalTemplateFailedCreateComponent,
        ZonalTemplateCreateComponent,
        ZonalRegionDirective,
        ScanThumbDirective
    ],
    imports: [
        BrowserModule,
        FormsModule,
        ToggleSwitchComponent,
        ReactiveFormsModule,
        QRCodeComponent,
        BaseChartDirective,
        NzButtonModule,
        NzDropdownModule,
        DXSettingsComponent,
        NzIconModule,
        NzInputModule,
        NzAlertModule,
        NzInputNumberModule,
        NzUploadComponent,
        NzUploadModule,
        NzTextareaCountComponent,
        NzSelectModule,
        NzFormModule,
        NzSkeletonModule,
        NzEmptyModule,
        NzSpaceModule,
        CommonModule,
        NzTableModule,
        NzTabsModule,
        NzCollapseModule,
        NzBadgeModule,
        NzSwitchModule,
        NzProgressModule,
        NzRadioModule,
        AppRoutingModule,
        NzModalModule,
        NzDatePickerModule,
        NzPopconfirmModule,
        NzCheckboxModule,
        NzTimePickerModule,
        NzTooltipModule,
        NzPopoverModule,
        NzTagModule,
        NzDescriptionsModule,
        NgbModule,
        JsonPipe,
        DatePipe,
        RouterModule,
        NzInputAddonBeforeDirective,
        NzColDirective,
        ScrollingModule,
        TextFieldModule
    ],
    providers: [
        {
            provide: UrlSerializer,
            useClass: LowerCaseUrlSerializer
        },
        NzNotificationService,
        provideNzIcons(icons),
        provideCharts(withDefaultRegisterables()),
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideNzI18n(en_US),
        provideAnimationsAsync(),
        {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
        {
            provide: APP_INITIALIZER,
            multi: true,
            deps: [AuthService, PerformaModulesService],
            useFactory: (auth: AuthService, modules: PerformaModulesService) => () =>
                auth.init().then(() => modules.init())
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        Monitor,
        provideNzDateFnsAdapter()
    ],
    bootstrap: [AppComponent]
})
export class AppModule {}
